import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';

import { useBridgewayApi } from '@/api/BridgewayApi';
import { useUserId } from '@/auth/UserIdContext';
import { logger } from '@/logger';

interface Notepad {
	name: string;
	content: string;
	updated_at: string;
}

interface Competitor {
	key: string;
	name: string;
}

interface CompetitorDetail {
	content: string;
	updated_at: string;
}

const NOTEPAD_LABELS: Record<string, string> = {
	brand_info: 'Brand Info',
	niche_playbook: 'Niche Playbook',
	customer_avatar: 'Customer Avatar',
	intel: 'Market Intelligence',
	core_behavior: 'Core Behavior',
};

const markdownComponents = {
	p: ({ children }: { children?: React.ReactNode }) => (
		<p
			style={{
				margin: 0,
				marginBottom: '0.5rem',
			}}
		>
			{children}
		</p>
	),
	h1: ({ children }: { children?: React.ReactNode }) => (
		<h1
			style={{
				fontSize: '1rem',
				fontWeight: 600,
				margin: '0.5rem 0',
			}}
		>
			{children}
		</h1>
	),
	h2: ({ children }: { children?: React.ReactNode }) => (
		<h2
			style={{
				fontSize: '0.875rem',
				fontWeight: 600,
				margin: '0.5rem 0',
			}}
		>
			{children}
		</h2>
	),
	h3: ({ children }: { children?: React.ReactNode }) => (
		<h3
			style={{
				fontSize: '0.75rem',
				fontWeight: 600,
				margin: '0.5rem 0',
			}}
		>
			{children}
		</h3>
	),
};

export const DemoSharedKnowledge = () => {
	const userId = useUserId();
	const bridgewayApi = useBridgewayApi();
	const [notepads, setNotepads] = useState<Notepad[]>([]);
	const [competitors, setCompetitors] = useState<
		Array<Competitor & { detail?: CompetitorDetail }>
	>([]);
	const [loadingNotepads, setLoadingNotepads] = useState(true);
	const [loadingCompetitors, setLoadingCompetitors] = useState(true);

	useEffect(() => {
		const fetchNotepads = async () => {
			if (!userId) return;

			try {
				setLoadingNotepads(true);
				const response = await bridgewayApi
					.get<Notepad[]>(`/api/user/${userId}/notepads/`)
					.catch(() => []);
				setNotepads(response);
			} catch (err) {
				logger.error('Failed to fetch notepads', err);
			} finally {
				setLoadingNotepads(false);
			}
		};

		fetchNotepads();
	}, [userId, bridgewayApi]);

	useEffect(() => {
		const fetchCompetitors = async () => {
			if (!userId) return;

			try {
				setLoadingCompetitors(true);
				const list = await bridgewayApi
					.get<Competitor[]>(`/api/user/${userId}/competitors/`)
					.catch(() => []);

				const competitorsWithDetails = await Promise.all(
					list.map(async (comp) => {
						try {
							const detail = await bridgewayApi.get<CompetitorDetail>(
								`/api/user/${userId}/competitors/${comp.key}`,
							);
							return { ...comp, detail };
						} catch {
							return comp;
						}
					}),
				);

				setCompetitors(competitorsWithDetails);
			} catch (err) {
				logger.error('Failed to fetch competitors', err);
			} finally {
				setLoadingCompetitors(false);
			}
		};

		fetchCompetitors();
	}, [userId, bridgewayApi]);

	return (
		<div
			style={{
				minHeight: '100vh',
				background: '#F7F7F7',
				padding: '2rem 1.5rem',
			}}
		>
			<div style={{ maxWidth: '1280px', margin: '0 auto' }}>
				<h1
					style={{
						fontSize: '2rem',
						fontWeight: 700,
						color: 'var(--text-primary)',
						marginBottom: '2rem',
						letterSpacing: '-0.02em',
					}}
				>
					Shared Knowledge
				</h1>

				{loadingNotepads ? (
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
							gap: '1rem',
							marginBottom: '2rem',
						}}
					>
						{[1, 2, 3, 4, 5].map((i) => (
							<div
								key={i}
								style={{
									padding: '1rem',
									background: '#f9fafb',
									border: '1px solid #e5e7eb',
									borderRadius: '8px',
									minHeight: '120px',
								}}
							>
								<div
									style={{
										width: '60%',
										height: '1rem',
										background: '#e5e7eb',
										borderRadius: '4px',
										marginBottom: '0.75rem',
									}}
								/>
								<div
									style={{
										width: '100%',
										height: '2.5rem',
										background: '#e5e7eb',
										borderRadius: '4px',
									}}
								/>
							</div>
						))}
					</div>
				) : (
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
							gap: '1rem',
							marginBottom: '2rem',
						}}
					>
						{Object.entries(NOTEPAD_LABELS).map(([key, label]) => {
							const notepad = notepads.find((n) => n.name === key);
							const hasContent = notepad && notepad.content.trim();

							return (
								<div
									key={key}
									style={{
										padding: '1rem',
										background: hasContent ? '#ffffff' : '#f9fafb',
										border: `1px solid ${hasContent ? '#e5e7eb' : '#e5e7eb'}`,
										borderRadius: '8px',
										minHeight: '120px',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<div
										style={{
											display: 'flex',
											justifyContent: 'space-between',
											alignItems: 'start',
											marginBottom: '0.75rem',
										}}
									>
										<h3
											style={{
												fontSize: '0.875rem',
												fontWeight: 600,
												color: 'var(--text-primary)',
											}}
										>
											{label}
										</h3>
										{notepad?.updated_at && (
											<span
												style={{
													fontSize: '0.625rem',
													color: 'var(--text-muted)',
													whiteSpace: 'nowrap',
												}}
											>
												{new Date(notepad.updated_at).toLocaleDateString(
													'en-US',
													{
														month: 'short',
														day: 'numeric',
													},
												)}
											</span>
										)}
									</div>
									<div
										style={{
											flex: 1,
											fontSize: '0.75rem',
											color: hasContent
												? 'var(--text-secondary)'
												: 'var(--text-muted)',
											lineHeight: 1.5,
										}}
									>
										{hasContent ? (
											<ReactMarkdown components={markdownComponents}>
												{notepad.content}
											</ReactMarkdown>
										) : (
											<span style={{ fontStyle: 'italic' }}>
												Not created yet
											</span>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}

				{!loadingCompetitors && competitors.length > 0 && (
					<>
						<div
							style={{
								marginTop: '1rem',
								marginBottom: '1.5rem',
							}}
						>
							<h2
								style={{
									fontSize: '1.25rem',
									fontWeight: 600,
									color: 'var(--text-primary)',
									marginBottom: '0.5rem',
								}}
							>
								Similar Businesses
							</h2>
							<p
								style={{
									fontSize: '0.875rem',
									color: 'var(--text-muted)',
									marginBottom: '0',
								}}
							>
								Intelligence and analysis on similar businesses
							</p>
						</div>

						<div
							style={{
								display: 'grid',
								gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
								gap: '1rem',
							}}
						>
							{competitors.map((competitor) => {
								const detail = competitor.detail;
								const hasContent = detail && detail.content.trim();

								return (
									<div
										key={competitor.key}
										style={{
											padding: '1rem',
											background: hasContent ? '#ffffff' : '#f9fafb',
											border: `1px solid ${hasContent ? '#e5e7eb' : '#e5e7eb'}`,
											borderRadius: '8px',
											minHeight: '120px',
											display: 'flex',
											flexDirection: 'column',
										}}
									>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'start',
												marginBottom: '0.75rem',
											}}
										>
											<h4
												style={{
													fontSize: '0.875rem',
													fontWeight: 600,
													color: 'var(--text-primary)',
												}}
											>
												{competitor.name || competitor.key}
											</h4>
											{competitor.detail?.updated_at && (
												<span
													style={{
														fontSize: '0.625rem',
														color: 'var(--text-muted)',
														whiteSpace: 'nowrap',
													}}
												>
													{new Date(
														competitor.detail.updated_at,
													).toLocaleDateString('en-US', {
														month: 'short',
														day: 'numeric',
													})}
												</span>
											)}
										</div>
										<div
											style={{
												flex: 1,
												fontSize: '0.75rem',
												color: hasContent
													? 'var(--text-secondary)'
													: 'var(--text-muted)',
												lineHeight: 1.5,
											}}
										>
											{hasContent ? (
												<ReactMarkdown components={markdownComponents}>
													{detail.content}
												</ReactMarkdown>
											) : (
												<span style={{ fontStyle: 'italic' }}>
													No research yet
												</span>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</>
				)}
			</div>
		</div>
	);
};
